import { useController } from 'react-hook-form';
import { useAppSelector } from '@global/store/hooks';
import { countriesSelector } from '@global/store/slices/country/countrySelector';
import { CountryService } from '@global/utils/CountryService';
import { getLocaleConfig } from '@global/localization/services';
import { CommonSelect } from '@src/global/components/common/CommonSelect';
import { SiteConfig } from '@src/global/utils/SiteConfig';

interface StateSelectProps {
  selectedState: string;
  fieldName: string;
  parentFieldName: string;
  disabled?: boolean;
  label?: string;
}

const STATE_SELECT_ID = 'card_billingaddress_state';
const STATE_LABEL_KEY = 'name';
const STATE_ID_KEY = 'id';
const STATE_VALUE_KEY = 'isoCodeShort';

export const StateSelectInput = ({
  selectedState,
  fieldName,
  disabled,
  parentFieldName,
  label,
}: StateSelectProps) => {
  const { field: countryField } = useController({ name: parentFieldName });
  const { iso } = SiteConfig.getConfig();
  const { stateDropdown, stateDropdownLabel, stateDropdownPlaceholder } =
    getLocaleConfig(countryField?.value || iso?.toUpperCase());

  const countries = useAppSelector(countriesSelector);
  const selectedCountry = CountryService.getCountryByIsoCode(
    countries,
    countryField.value
  );

  const defaultState =
    selectedCountry?.regions?.find(
      (regin) =>
        regin.name.toLowerCase() === selectedState?.toLowerCase() ||
        regin.isoCodeShort.toLowerCase() === selectedState?.toLowerCase()
    )?.isoCodeShort || '';

  const { field: stateField, fieldState } = useController({
    name: fieldName,
    defaultValue: defaultState,
  });

  const { field: schoolField } = useController({ name: 'school' });
  const stateOptions =
    selectedCountry?.isoCodeTwo?.toUpperCase() === 'CN'
      ? [...(selectedCountry?.regions || [])].sort((a, b) =>
          (a?.name || '').localeCompare(b?.name || '', undefined, {
            sensitivity: 'base',
          })
        )
      : selectedCountry?.regions;

  if (!stateDropdown) return null;

  return (
    <CommonSelect
      label={label || stateDropdownLabel}
      id={STATE_SELECT_ID}
      placeholder={stateDropdownPlaceholder}
      options={stateOptions}
      onChange={(value) => {
        stateField.onChange(value);
        schoolField.onChange(''); // reset school when state is changed
      }}
      itemValue={STATE_VALUE_KEY}
      itemLabel={STATE_LABEL_KEY}
      itemId={STATE_ID_KEY}
      disabled={disabled}
      value={stateField.value}
      isValid={stateField.value && !fieldState.error}
      error={!!fieldState.error}
      errorMessage={fieldState.error?.message}
    />
  );
};
